import React from "react";
import { getLegalReparentTargets } from "../../behavior/tree";
import { resolveEndEffectorBodyUuid } from "../../behavior/endEffectorResolve";
import { getBehaviorTrainedModel } from "../../behavior/trainedModels";
import { useTrainedModelRuntimeStatus } from "../../behavior/trainedModelRuntimeStatus";
import { useSceneStore } from "../../stores/sceneStore";
import { buildBodyParentMap } from "../../utils/bodyHierarchy";
import { CATEGORY_COLORS, KIND_LABELS, getCategory } from "./node-inspector/nodeInspectorConstants";
import { INPUT_CLASS, Row } from "./node-inspector/NodeInspectorFields";
import NodeInspectorHeader from "./node-inspector/NodeInspectorHeader";
import NodeParameterFields from "./node-inspector/NodeParameterFields";
import NodeStructureControls from "./node-inspector/NodeStructureControls";
import PickPlaceSetupSection from "./node-inspector/PickPlaceSetupSection";
import { resolvePickPlaceSetupNodes } from "./node-inspector/pickPlaceSetup";

export default function NodeInspectorCard({
  node,
  bodies,
  behaviorDoc = null,
  onApplyDocument = null,
  onChange,
  onClose,
  onDelete,
  canDelete = true,
}) {
  const opts = Object.values(bodies || {}).map((b) => ({ value: b.uuid, label: b.name || b.uuid }));
  const jointsByUuid = useSceneStore((s) => s.joints || {});
  const sitesByName = useSceneStore((s) => s.sites || {});
  const eeOpts = React.useMemo(() => {
    const parentMap = buildBodyParentMap(bodies || {});
    const hasScalarJointOnPath = (bodyUuid) => {
      let current = bodyUuid || null;
      while (current) {
        const body = bodies?.[current];
        if (!body) break;
        const jointRefs = Array.isArray(body.joints) ? body.joints : [];
        const hasScalar = jointRefs.some((jointRef) => {
          const jointUuid =
            typeof jointRef === "string"
              ? jointRef
              : jointRef && typeof jointRef === "object"
                ? jointRef.uuid
                : null;
          const joint = jointUuid ? jointsByUuid?.[jointUuid] : null;
          return joint?.kind === "hinge" || joint?.kind === "slide";
        });
        if (hasScalar) return true;
        current = parentMap[current] ?? null;
      }
      return false;
    };
    const bodyOptions = Object.values(bodies || {})
      .filter((b) => hasScalarJointOnPath(b.uuid))
      .map((b) => ({
        value: b.uuid,
        label: `Body: ${b.name || b.uuid}`,
      }));
    return [{ value: "gripper_target", label: "gripper_target (site)" }, ...bodyOptions];
  }, [bodies, jointsByUuid]);
  const trainedModel =
    node.kind === "runTrainedModel" ? getBehaviorTrainedModel(node.modelId) : null;
  const trainedModelRuntime = useTrainedModelRuntimeStatus(node?.id || "", {
    enabled: node.kind === "runTrainedModel",
  });
  const defaultKindName =
    node.kind === "runTrainedModel"
      ? trainedModel?.name || "Trained Model"
      : KIND_LABELS[node.kind] || node.kind;
  const labelValue = typeof node.label === "string" ? node.label : "";
  const displayName = labelValue.trim().length > 0 ? labelValue : defaultKindName;
  const category = getCategory(node.kind);
  const accent = CATEGORY_COLORS[category] || CATEGORY_COLORS.default;
  const isRoot = behaviorDoc ? behaviorDoc.rootId === node.id : false;
  const eeScene = React.useMemo(
    () => ({
      bodies: bodies || {},
      sites: sitesByName || {},
    }),
    [bodies, sitesByName]
  );
  const bodyExists = React.useCallback(
    (uuid) => typeof uuid === "string" && uuid.length > 0 && Boolean(bodies?.[uuid]),
    [bodies]
  );
  const endEffectorExists = React.useCallback(
    (endEffectorRef) =>
      typeof endEffectorRef === "string" &&
      endEffectorRef.trim().length > 0 &&
      resolveEndEffectorBodyUuid(eeScene, endEffectorRef) != null,
    [eeScene]
  );

  const currentParentId = React.useMemo(() => {
    if (!behaviorDoc) return null;
    for (const [id, n] of Object.entries(behaviorDoc.nodes)) {
      if (n.kind === "sequence" || n.kind === "selector") {
        if (n.children.includes(node.id)) return id;
      } else if ((n.kind === "retry" || n.kind === "repeat") && n.child === node.id) {
        return id;
      } else if (n.kind === "ifThenElse") {
        if (n.conditionChild === node.id || n.thenChild === node.id || n.elseChild === node.id)
          return id;
      }
    }
    return null;
  }, [behaviorDoc, node.id]);

  const reparentTargets = React.useMemo(() => {
    if (!behaviorDoc || isRoot) return [];
    return getLegalReparentTargets(behaviorDoc, node.id).filter((id) => id !== node.id);
  }, [behaviorDoc, node.id, isRoot]);
  const pickPlaceSetup = React.useMemo(
    () => (behaviorDoc ? resolvePickPlaceSetupNodes(behaviorDoc) : null),
    [behaviorDoc]
  );
  const [fieldErrors, setFieldErrors] = React.useState({});

  const getFieldError = (fieldKey) => fieldErrors[fieldKey] || "";
  const setFieldError = (fieldKey, message) => {
    setFieldErrors((prev) => {
      const current = prev[fieldKey] || "";
      if (current === message) return prev;
      if (!message) {
        if (!(fieldKey in prev)) return prev;
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      }
      return { ...prev, [fieldKey]: message };
    });
  };
  const validateNumeric = ({ fieldKey, raw, min = -Infinity, max = Infinity, integer = false }) => {
    if (raw === "" || raw === "-" || raw === "." || raw === "-.") {
      setFieldError(fieldKey, "Enter a valid number.");
      return null;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {
      setFieldError(fieldKey, "Enter a valid number.");
      return null;
    }
    if (integer && !Number.isInteger(parsed)) {
      setFieldError(fieldKey, "Must be a whole number.");
      return null;
    }
    if (parsed < min || parsed > max) {
      if (Number.isFinite(min) && Number.isFinite(max)) {
        setFieldError(fieldKey, `Must be between ${min} and ${max}.`);
      } else if (Number.isFinite(min)) {
        setFieldError(fieldKey, `Must be >= ${min}.`);
      } else if (Number.isFinite(max)) {
        setFieldError(fieldKey, `Must be <= ${max}.`);
      } else {
        setFieldError(fieldKey, "Out of range.");
      }
      return null;
    }
    setFieldError(fieldKey, "");
    return parsed;
  };

  return (
    <div
      data-behavior-inspector="true"
      className="absolute top-3 right-3 z-20 w-[300px] max-h-[calc(100%-24px)] overflow-y-auto rounded-lg border border-gray-700 shadow-xl"
      style={{ backgroundColor: "var(--panel-surface)", backdropFilter: "blur(10px)" }}
    >
      <NodeInspectorHeader
        accent={accent}
        defaultKindName={defaultKindName}
        displayName={displayName}
        onClose={onClose}
        onDelete={onDelete}
        canDelete={canDelete}
      />
      <div className="space-y-4 p-4 text-sm text-gray-200">
        <Row label="Name">
          <input
            className={INPUT_CLASS}
            value={labelValue}
            onChange={(e) => onChange({ ...node, label: e.target.value })}
            placeholder={defaultKindName}
          />
        </Row>
        {pickPlaceSetup && behaviorDoc && onApplyDocument ? (
          <PickPlaceSetupSection
            behaviorDoc={behaviorDoc}
            eeOpts={eeOpts}
            getFieldError={getFieldError}
            onApplyDocument={onApplyDocument}
            opts={opts}
            pickPlaceSetup={pickPlaceSetup}
            validateNumeric={validateNumeric}
          />
        ) : null}
        <NodeStructureControls
          behaviorDoc={behaviorDoc}
          currentParentId={currentParentId}
          isRoot={isRoot}
          node={node}
          onApplyDocument={onApplyDocument}
          reparentTargets={reparentTargets}
        />
        <NodeParameterFields
          bodyExists={bodyExists}
          eeOpts={eeOpts}
          endEffectorExists={endEffectorExists}
          getFieldError={getFieldError}
          node={node}
          onChange={onChange}
          opts={opts}
          trainedModel={trainedModel}
          trainedModelRuntime={trainedModelRuntime}
          validateNumeric={validateNumeric}
        />
      </div>
    </div>
  );
}
